import { useEffect, useMemo, useState } from "react";
import { CircleMarker, MapContainer, TileLayer } from "react-leaflet";
import { toast } from "react-toastify";
import { useTaxiActions, useUIActions } from "../store/useActions";
import { useTaxiSelector, useUISelector, useUserSelector } from "../store/useSelectors";
import { LocationPicker } from "./LocationPicker";
import BaseNavbar from "./BaseNavbar";
import CurrentLocationMarker from "./CurrentLocationMarker";
import { coreGetPrice } from "../api/core";
import { sendHapticFeedback } from "../services/telegram";
import { routingCreateControl } from "../services/routing";
import { useMap } from 'react-leaflet/hooks';
import { nominatimReverse } from "../api/nominatim";
import { nominatimProcessResult } from "../services/nominatim";
import Cookies from "js-cookie";

const CreateOrderRouting = () => {
    const map = useMap();
    const [routing, setRouting] = useState();
    const { startLocation, endLocation } = useTaxiSelector();
    // !!!!!!
    const { setRouteDisplayed } = useUIActions();

    useEffect(() => {
        drawRoute();
    }, [startLocation, endLocation]);

    const drawRoute = () => {
        if (!map || !startLocation || !endLocation) {
            // !!!!!!
            setRouteDisplayed(false);
            if (routing) {
                map.removeControl(routing);
                setRouting(null);
            }
            return;
        }

        if (routing) {
            routing.spliceWaypoints(0, 2);
            map.removeControl(routing);
        }
        const newRouting = routingCreateControl(startLocation, endLocation).addTo(map);
        // !!!!!!
        newRouting.on("routesfound", () => setRouteDisplayed(true));
        setRouting(newRouting);
    };

    return null;
};

const CreateOrder = () => {
    const [map, setMap] = useState();
    const [price, setPrice] = useState(null);
    const [duration, setDuration] = useState(null);
    const [loading, setLoading] = useState(false);
    // Определяем платформу: если есть telegram_auth_token в URL — Telegram, иначе MAX
    const isMaxPlatform = !new URLSearchParams(window.location.search).get("telegram_auth_token");
    const [priorityAmounts, setPriorityAmounts] = useState([]);
    const { centerPosition } = useUISelector();
    const { setCenterPosition } = useUIActions();
    const { setStartAddress, setStartLocation, setCurrentLocation } = useTaxiActions();
    const { token: userAuthToken, data: userData } = useUserSelector();
    const {
        startAddress,
        startLocation,
        endAddress,
        endLocation,
        paymentMethod,
        babyChair,
        emptyTrunk,
        animalTransportation,
        driverAssistance,
        comment,
        coupon,
        clientPhone,
        startAddressEntrance,
        femaleCrew,
        priorityAmount,
    } = useTaxiSelector();

    const params = new URLSearchParams(window.location.search);
    const isManagerMode = params.get("manager") === "1" || params.get("WebAppStartParam") === "manager";

    const applyPosition = (latitude, longitude) => {
        if (!latitude || !longitude || isNaN(latitude) || isNaN(longitude)) {
            console.warn("Invalid coordinates:", latitude, longitude);
            return;
        }
        setCenterPosition([latitude, longitude]);
        setCurrentLocation({ latitude: parseFloat(latitude), longitude: parseFloat(longitude) });
        // Живое GPS в куку → поиск адресов сортирует подсказки по близости к юзеру.
        Cookies.set("user-geo", `${latitude},${longitude}`, { expires: 1 });
        if (map) {
            map.flyTo([latitude, longitude], 15, { duration: 0.5 });
        }
        nominatimReverse(latitude, longitude)
            .then((response) => response.data)
            .then((resultJson) => {
                if (!resultJson) return;
                const result = nominatimProcessResult(resultJson);
                Cookies.set("start-address-state", result.address?.state ?? "", { expires: 2 });
                Cookies.set("start-address-city", (result.address?.city || result.address?.town) ?? "", { expires: 2 });
                setStartAddress(result.set_name);
                setStartLocation({
                    latitude: parseFloat(latitude),
                    longitude: parseFloat(longitude),
                });
            })
            .catch((error) => console.error("Не удалось определить адрес:", error));
    };

    const ipGeolocation = () => {
        console.log("[GEO] Trying IP-based geolocation...");
        fetch("https://ipapi.co/json/", { timeout: 5000 })
            .then(r => r.json())
            .then(data => {
                if (data && data.latitude && data.longitude) {
                    console.log("[GEO] IP location:", data.latitude, data.longitude, data.city);
                    applyPosition(data.latitude, data.longitude);
                } else {
                    console.warn("[GEO] IP geolocation: no coords");
                    toast.info("Выберите адрес на карте");
                }
            })
            .catch(e => {
                console.error("[GEO] IP geolocation failed:", e);
                toast.info("Выберите адрес на карте");
            });
    };

    const fallbackGeolocation = () => {
        if (!navigator.geolocation) {
            toast.error("Геолокация не поддерживается");
            return;
        }
        console.log("[GEO] Requesting geolocation...");
        // Единый запрос с высокой точностью и большим таймаутом
        navigator.geolocation.getCurrentPosition(
            (position) => {
                console.log("[GEO] Success:", position.coords.latitude, position.coords.longitude);
                applyPosition(position.coords.latitude, position.coords.longitude);
            },
            (error) => {
                console.error("[GEO] Error:", error.code, error.message);
                if (error.code === 1) {
                    // PERMISSION_DENIED — но в MAX это может быть баг WebView
                    // Пробуем через watchPosition как workaround
                    console.log("[GEO] Trying watchPosition workaround...");
                    const watchId = navigator.geolocation.watchPosition(
                        (pos) => {
                            console.log("[GEO] watchPosition success:", pos.coords.latitude, pos.coords.longitude);
                            navigator.geolocation.clearWatch(watchId);
                            applyPosition(pos.coords.latitude, pos.coords.longitude);
                        },
                        (err2) => {
                            console.error("[GEO] watchPosition also failed:", err2.code, err2.message);
                            navigator.geolocation.clearWatch(watchId);
                            if (!window.__geoToastShown) {
                                window.__geoToastShown = true;
                                toast.error("Не удалось определить геолокацию. Выберите адрес вручную.");
                                setTimeout(() => { window.__geoToastShown = false; }, 15000);
                            }
                        },
                        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
                    );
                    // Таймаут для watchPosition
                    setTimeout(() => { navigator.geolocation.clearWatch(watchId); }, 12000);
                } else if (error.code === 2) {
                    toast.error("Геолокация недоступна. Выберите адрес вручную.");
                } else {
                    toast.error("Таймаут геолокации. Попробуйте ещё раз.");
                }
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
        );
    };

    const getCurrentPosition = () => {
        const tg = window.Telegram?.WebApp;
        const lm = tg?.LocationManager;  // Only Telegram has LocationManager

        const isMax = !window.Telegram?.WebApp && window.WebApp;
        if (lm && !isMax) {
            let handled = false;
            // Таймаут: если LocationManager не ответил за 5 сек — fallback
            const timer = setTimeout(() => {
                if (!handled) { handled = true; fallbackGeolocation(); }
            }, 5000);

            try {
                lm.init(() => {
                    if (handled) return;
                    handled = true;
                    clearTimeout(timer);
                    if (lm.isLocationAvailable && lm.isAccessGranted) {
                        lm.getLocation((data) => {
                            if (data) {
                                applyPosition(data.latitude, data.longitude);
                            } else {
                                fallbackGeolocation();
                            }
                        });
                    } else if (lm.isLocationAvailable && !lm.isAccessGranted) {
                        // Попросить у пользователя разрешение
                        lm.getLocation((data) => {
                            if (data) {
                                applyPosition(data.latitude, data.longitude);
                            } else {
                                toast.error("Разрешите доступ к геолокации");
                            }
                        });
                    } else {
                        fallbackGeolocation();
                    }
                });
            } catch (e) {
                clearTimeout(timer);
                if (!handled) { handled = true; fallbackGeolocation(); }
            }
        } else {
            // MAX: try requesting permission explicitly first
            if (navigator.permissions) {
                navigator.permissions.query({ name: "geolocation" }).then((result) => {
                    console.log("[GEO] Permission status:", result.state);
                    if (result.state === "denied") {
                        console.log("[GEO] Permission denied, trying IP...");
                        ipGeolocation();
                    } else {
                        fallbackGeolocation();
                    }
                }).catch(() => fallbackGeolocation());
            } else {
                fallbackGeolocation();
            }
        }
    };

    // Auto-geolocation on mount (Telegram only)
    useEffect(() => {
        const isMax = !new URLSearchParams(window.location.search).get("telegram_auth_token");
        if (!isMax) {
            // Telegram: auto-detect location
            getCurrentPosition();
        }
    }, []);

    // MAX: show hint to tap on map (geolocation doesn't work in MAX WebView)
    useEffect(() => {
        const isMax = !new URLSearchParams(window.location.search).get("telegram_auth_token");
        if (isMax && !window.__maxHintShown) {
            window.__maxHintShown = true;
            setTimeout(() => {
                toast.info("\u041d\u0430\u0436\u043c\u0438\u0442\u0435 \u043d\u0430 \u043a\u0430\u0440\u0442\u0443 \u0447\u0442\u043e\u0431\u044b \u0432\u044b\u0431\u0440\u0430\u0442\u044c \u0442\u043e\u0447\u043a\u0443 \u043e\u0442\u043f\u0440\u0430\u0432\u043b\u0435\u043d\u0438\u044f", { autoClose: 5000 });
            }, 1500);
        }
    }, []);

    // Сбросить размер карты после монтирования
    useEffect(() => {
        if (!map) return;
        setTimeout(() => {
            map.invalidateSize();
        }, 300);
    }, [map]);

    // Автоматический переход к местоположению при открытии карты
    useEffect(() => {
        if (map && centerPosition && !isNaN(centerPosition[0]) && !isNaN(centerPosition[1])) {
            map.flyTo(centerPosition, 15, { duration: 0.5 });
        }
    }, [map, centerPosition]);

    useEffect(() => {
        if (startLocation && endLocation) {
            console.log("[PRICE CALC] start:", startLocation, "end:", endLocation, "token:", userAuthToken);
            setLoading(true);
            coreGetPrice(
                startLocation,
                endLocation,
                babyChair,
                emptyTrunk,
                animalTransportation,
                driverAssistance,
                coupon,
                userAuthToken,
                femaleCrew,
                priorityAmount
            )
                .then((response) => {
                    console.log("[PRICE OK]", response.data);
                    setPrice(response.data.cost);
                    setDuration(response.data.duration);
                    if (response.data.priority_amounts) {
                        setPriorityAmounts(response.data.priority_amounts);
                    }
                    setLoading(false);
                })
                .catch((error) => {
                    console.error("[PRICE ERROR]", error?.response?.status, error?.response?.data, error?.message, error);
                    setLoading(false);
                    const detail = error?.response?.data?.detail || error?.message || "неизвестная ошибка";
                    toast.error("Не удалось рассчитать стоимость: " + detail);
                });
        } else {
            setPrice(null);
            setDuration(null);
        }
    }, [
        startLocation,
        endLocation,
        babyChair,
        emptyTrunk,
        animalTransportation,
        driverAssistance,
        coupon,
        userAuthToken,
        femaleCrew,
        priorityAmount,
    ]);

    // Автоматическое перемещение карты при изменении локаций
    useEffect(() => {
        if (!map) return;
        
        if (startLocation && endLocation) {
            // Перемещаем карту так, чтобы были видны обе точки
            const bounds = [
                [startLocation.latitude, startLocation.longitude],
                [endLocation.latitude, endLocation.longitude]
            ];
            map.flyToBounds(bounds, { padding: [50, 50], duration: 0.5 });
        } else if (startLocation) {
            if (!isNaN(startLocation.latitude) && !isNaN(startLocation.longitude)) map.flyTo([startLocation.latitude, startLocation.longitude], 15, { duration: 0.5 });
        } else if (endLocation) {
            if (!isNaN(endLocation.latitude) && !isNaN(endLocation.longitude)) map.flyTo([endLocation.latitude, endLocation.longitude], 15, { duration: 0.5 });
        }
    }, [map, startLocation, endLocation]);

    const createOrder = () => {
        sendHapticFeedback();

        if (isManagerMode && (!clientPhone || !clientPhone.trim())) {
            toast.error("Для создания заказа за клиента необходимо указать номер телефона клиента");
            return;
        }

        if (!startLocation || !endLocation) {
            toast.error("Укажите адреса отправления и назначения");
            return;
        }

        if (!startAddress || !endAddress) {
            toast.error("Укажите адреса отправления и назначения");
            return;
        }

        const orderData = {
            start_location: startLocation,
            end_location: endLocation,
            start_address: startAddress,
            end_address: endAddress,
            payment_method: paymentMethod,
            baby_chair: babyChair,
            empty_trunk: emptyTrunk,
            animal_transportation: animalTransportation,
            driver_assistance: driverAssistance,
            female_crew: femaleCrew,
            comment: comment || "",
            entrance: startAddressEntrance || "",
            coupon: coupon || null,
            priority_amount: priorityAmount || 0,
        };

        if (isManagerMode) {
            orderData.created_by_manager = true;
            orderData.client_phone = clientPhone.trim();
        }

        try {
            const tg = isMaxPlatform ? null : window.Telegram?.WebApp;
            const maxApp = (!tg && window.WebApp) ? window.WebApp : null;
            if (tg) {
                tg.sendData(JSON.stringify(orderData));
                tg.close();
            } else if (maxApp) {
                // MAX: отправляем через специальный эндпоинт с max_chat_id
                const maxUserId = maxApp.initDataUnsafe?.user?.id;
                const maxOrderData = { ...orderData, max_chat_id: maxUserId };
                console.log("[MAX ORDER] sending:", maxOrderData);
                fetch(window.location.origin + '/core/api/orders/createOrderFromMax/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(maxOrderData),
                }).then(async r => {
                    const data = await r.json().catch(() => ({}));
                    if (r.ok) {
                        toast.success("Заказ #" + (data.id || "") + " создан!");
                        setTimeout(() => { try { maxApp.close(); } catch(e) { window.close(); } }, 1500);
                    } else {
                        console.error("[MAX ORDER] error:", data);
                        toast.error("Ошибка: " + (data.detail || "неизвестно"));
                    }
                }).catch(e => {
                    console.error("[MAX ORDER] network:", e);
                    toast.error("Ошибка сети");
                });
            } else {
                toast.error("Мессенджер не определён");
            }
        } catch (error) {
            console.error("[ORDER ERROR]", error?.response?.status, error?.response?.data, error?.message, error);
            toast.error("Ошибка заказа: " + (error?.response?.data?.detail || error?.message || "неизвестно"));
        }
    };

    const safeCenterPosition = (centerPosition && !isNaN(centerPosition[0]) && !isNaN(centerPosition[1]))
        ? centerPosition : [54.9, 52.3]; // Fallback: Нижнекамск

    const displayMap = useMemo(
        () => (
            <MapContainer
                center={safeCenterPosition}
                zoom={13}
                style={{ width: "100vw", height: "100vh" }}
                ref={setMap}
            >
                <CreateOrderRouting />
                <LocationPicker />
                <CurrentLocationMarker />
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {startLocation && startLocation.latitude != null && startLocation.longitude != null && (
                    <CircleMarker
                        center={[startLocation.latitude, startLocation.longitude]}
                        pathOptions={{ color: "red" }}
                        radius={5}
                    />
                )}
                {endLocation && endLocation.latitude != null && endLocation.longitude != null && (
                    <CircleMarker
                        center={[endLocation.latitude, endLocation.longitude]}
                        pathOptions={{ color: "grey" }}
                        radius={5}
                    />
                )}
            </MapContainer>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const isFormValid = startLocation && endLocation && startAddress && endAddress && (!isManagerMode || (clientPhone && clientPhone.trim()));

    // Обновление MainButton (only Telegram)
    useEffect(() => {
        if (isMaxPlatform) return; // MAX не имеет MainButton
        const mainButton = window.Telegram?.WebApp?.MainButton;
        if (!mainButton) return;

        const handleClick = () => {
            createOrder();
        };

        if (price !== null && isFormValid) {
            const durationText = duration && duration !== "Not found" ? ` • ~${duration} мин` : "";
            const buttonText = isManagerMode 
                ? `Создать заказ • ${price} ₽${durationText}`
                : `Заказать такси • ${price} ₽${durationText}`;
            
            mainButton.setText(buttonText);
            // Workaround: Android не обновляет MainButton динамически
            mainButton.hide();
            mainButton.show();
            mainButton.onClick(handleClick);
        } else {
            mainButton.hide();
        }

        return () => {
            if (mainButton) {
                mainButton.offClick(handleClick);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [price, duration, isFormValid, isManagerMode, startLocation, endLocation, startAddress, endAddress, clientPhone, femaleCrew, babyChair, emptyTrunk, animalTransportation, driverAssistance, priorityAmount, coupon, comment, startAddressEntrance, paymentMethod]);

    return (
        <>
            {displayMap}
            <BaseNavbar isManagerOrder={isManagerMode} getCurrentPosition={getCurrentPosition} priorityAmounts={priorityAmounts} currentPrice={price} isMaxPlatform={isMaxPlatform} price={price} duration={duration} isFormValid={isFormValid} onCreateOrder={createOrder} />

        </>
    );
};

export default CreateOrder;
